<script setup>
  import ArrowRight from '@/assets/icons/sidebar/arrow-right.svg'
  import { useI18n } from 'vue-i18n'

  import { ref } from 'vue'

  import SidebarPanelIcon from '@/features/layout/components/SidebarPanelIcon.vue'
  import SidebarPanelLink from '@/features/layout/components/SidebarPanelLink.vue'
  import { useLayoutStore } from '@/features/layout/useLayoutStore.js'
  import ClubRouterLink from '@/features/router/ClubRouterLink.vue'

  const props = defineProps(['item'])
  const layoutStore = useLayoutStore()
  const { to, icon, children, label, iconClass } = props.item
  const isOpened = ref(true)

  const { t } = useI18n()

  function handleToggle() {
    isOpened.value = !isOpened.value
  }

  const As = to ? ClubRouterLink : 'span'
</script>

<template>
  <li class="group-item">
    <component
      :is="As"
      v-tooltip.right="layoutStore.sidebarOpened ? '' : t(label)"
      class="group-link"
      :to="to"
      :class="{
        centered: !layoutStore.sidebarOpened,
      }"
      @click.prevent="handleToggle"
    >
      <span class="group-icon">
        <SidebarPanelIcon :icon="icon" :icon-class="iconClass" />
      </span>
      <template v-if="layoutStore.sidebarOpened">
        <div class="group-item-name">{{ t(label) }}</div>
        <span v-if="children?.length" class="toggle">
          <ArrowRight />
        </span>
      </template>
    </component>

    <ul class="group-item-children">
      <SidebarPanelLink
        v-for="subItem of children"
        :key="subItem.to || subItem.label"
        :to="subItem.to"
        :icon="subItem.icon"
        :icon-class="subItem.iconClass"
        >{{ t(subItem.label) }}</SidebarPanelLink
      >
    </ul>
  </li>
</template>

<style lang="scss" scoped>
  .group-item {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    list-style: none;
    position: relative;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    &:hover .group-item-children {
      visibility: visible;
    }
  }

  .group-link {
    display: flex;
    padding: 0 16px;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    color: var(--fm-ui-sidebar-group-link-color);
    height: 45px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 6px;
      height: 100%;
      border-left: 6px solid transparent;
    }

    &.centered {
      justify-content: center;
    }

    &.router-link-active {
      background: var(--fm-ui-sidebar-group-link-active-background-color);
      color: var(--fm-ui-sidebar-group-link-active-color);

      &::before {
        border-left: 6px solid var(--fm-ui-sidebar-group-link-active-border-color);
      }
    }

    &:hover {
      background: var(--fm-ui-sidebar-group-link-hover-background-color);
      color: var(--fm-ui-sidebar-group-link-hover-color);
    }

    &:focus {
      background: var(--fm-ui-sidebar-group-link-focus-background-color);
      color: var(--fm-ui-sidebar-group-link-focus-color);
    }

    &:focus-within {
      background: var(--fm-ui-sidebar-group-link-focus-background-color);
      color: var(--fm-ui-sidebar-group-link-focus-color);
    }

    @include focusRing;
  }

  .group-icon {
    width: 16px;
    height: 16px;
  }

  .toggle {
    width: 30px;
    height: 100%;
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }

  .group-item-children {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: absolute;
    left: 100%;
    background: var(--fm-ui-sidebar-group-children-background-color);
    visibility: hidden;
    min-width: 150px;
    border-bottom-right-radius: 8px;
    border-top-right-radius: 8px;
  }
</style>
